import { useEffect, useRef } from 'react';

import { useChatModeStoreActions } from '@atlassian/conversation-assistant-chat-prompt-input/useChatModeStoreActions';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	useEmptyChatLaunchSelector,
	useUIStoreActions,
} from '@atlassian/conversation-assistant-store/controllers/ui-store';

/**
 * Manages the lifecycle of an empty-chat launch (e.g. project card → chat).
 *
 * - When the conversation transitions from empty to active (after the first message),
 *   persists the context banner preference and clears the one-shot greeting.
 *   Project context persists so the banner keeps showing the entity URL.
 * - When returning to EMPTY_CONVERSATION_ID without a new launch, clears all stale state.
 */
export function useEmptyChatLaunchLifecycle(currentConversationId: string): void {
	const [{ projectContext: emptyLaunchProjectContext, launchId: emptyLaunchId }] =
		useEmptyChatLaunchSelector();
	const { clearEmptyChatLaunchGreeting, clearEmptyChatLaunchAll, checkAndUpdateLifecycleGuard } =
		useUIStoreActions();
	const prevLaunchIdRef = useRef(emptyLaunchId);

	const { setChatMode } = useChatModeStoreActions();

	useEffect(() => {
		const shouldRun = checkAndUpdateLifecycleGuard(currentConversationId, emptyLaunchId);
		if (!shouldRun) {
			return;
		}

		if (currentConversationId !== EMPTY_CONVERSATION_ID) {
			if (emptyLaunchProjectContext) {
				setChatMode(currentConversationId, { useCurrentPageContext: true });
			}
			clearEmptyChatLaunchGreeting();
		} else if (emptyLaunchId === prevLaunchIdRef.current) {
			// Returning to empty state without a new launch — clear stale context
			clearEmptyChatLaunchAll();
		}
		prevLaunchIdRef.current = emptyLaunchId;
	}, [
		currentConversationId,
		clearEmptyChatLaunchGreeting,
		clearEmptyChatLaunchAll,
		emptyLaunchProjectContext,
		emptyLaunchId,
		setChatMode,
		checkAndUpdateLifecycleGuard,
	]);
}
